import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  useNetworkStatus,
  useUpdateDetection,
  useServiceWorkerRegistration,
} from './hooks';

// ---- Types -----

type OfflineStatus = 'ready' | 'not-ready' | 'stale';

interface NetworkContextType {
  networkStatus: boolean;
  setNetworkStatus: React.Dispatch<React.SetStateAction<boolean>>;

  offlineStatus: OfflineStatus;
  isReady: boolean;
  isStale: boolean;
  isNotReady: boolean;

  updateState: string;
  isUpdateAvailable: boolean;
  triggerUpdate: () => void;
}

const NetworkContext = createContext<NetworkContextType | null>(null);

const STORAGE_KEY = 'offline-status';

type RegisterSWFn = (options?: object) => (reload?: boolean) => Promise<void>;

interface Props {
  registerSW: RegisterSWFn;
  children: React.ReactNode;
}

// ---- Provider ----

export const NetworkContextProvider: React.FC<Props> = ({
  registerSW,
  children,
}) => {
  // Offline status — kept as-is for backwards compat
  const [offlineStatus, setOfflineStatus] = useState<OfflineStatus>(
    (localStorage.getItem(STORAGE_KEY) as OfflineStatus) || 'not-ready'
  );

  const persistOfflineStatus = useCallback((status: OfflineStatus) => {
    localStorage.setItem(STORAGE_KEY, status);
    setOfflineStatus(status);
  }, []);

  // Shared ref — passed into both registration and detection hooks
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Hook: update detection
  const { updateState, isUpdateAvailable, triggerUpdate, markUpdateAvailable } =
    useUpdateDetection(registrationRef);

  // Hook: SW registration — wires into update detection callbacks
  useServiceWorkerRegistration(registerSW, {
    registrationRef,
    onOfflineReady: () => persistOfflineStatus('ready'),
    onUpdateAvailable: () => {
      persistOfflineStatus('stale');
      markUpdateAvailable();
    },
  });

  // Hook: network status
  const networkStatus = useNetworkStatus();

  // Auto-trigger update on reconnect if stale (preserved from original)
  useEffect(() => {
    if (networkStatus && offlineStatus === 'stale') {
      markUpdateAvailable();
    }
  }, [networkStatus, offlineStatus, markUpdateAvailable]);

  return (
    <NetworkContext.Provider
      value={{
        networkStatus,
        setNetworkStatus: () => {},

        offlineStatus,
        isReady: offlineStatus === 'ready',
        isStale: offlineStatus === 'stale',
        isNotReady: offlineStatus === 'not-ready',

        updateState,
        isUpdateAvailable,
        triggerUpdate,
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
};

// ---- Hook  ----

export const useNetworkContext = () => {
  const ctx = useContext(NetworkContext);
  if (!ctx) {
    throw new Error(
      'useNetworkContext must be used inside NetworkContextProvider'
    );
  }
  return ctx;
};
